import { airlineName, defaultCheckoutTime, hotelBrand } from '@trips/shared/brands';
import { newId } from '@trips/shared/ids';
import { ITEM_TYPE_LABELS } from '@trips/shared/items';
import type { Cost, Item, ItemDetails, ItemType, Place } from '@trips/shared/schema';
import { addDays, instantFromLocal, localDate, localTime, tzAbbr } from '@trips/shared/time';
import { useMemo, useState } from 'react';
import { put } from '../../data/mutate';
import {
  selectHome,
  selectPlaceLookup,
  selectToday,
  selectTripSummary,
} from '../../data/selectors';
import { getState, useStore } from '../../data/store';
import { tripForNewItem, widenTripFor } from '../../data/trips';
import { deviceTimeZone, haptics } from '../../lib/platform';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { FieldRow, Select, TextArea, TextField } from '../../ui/Field';
import { Icon, type IconName } from '../../ui/Icon';
import { ITEM_ICON } from '../../ui/itemIcon';
import { Segmented } from '../../ui/Segmented';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/Toast';
import { AirportField, PlaceField } from './Pickers';
import s from './PlanEditor.module.css';

const TYPES: { type: ItemType; icon: IconName }[] = (Object.keys(ITEM_ICON) as ItemType[]).map(
  (type) => ({ type, icon: ITEM_ICON[type] }),
);

interface Draft {
  type: ItemType;
  title: string;
  provider: string;
  confirmation: string;
  phone: string;
  url: string;
  notes: string;
  startPlace: Place | null;
  endPlace: Place | null;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  // type-specific
  airline: string;
  flightNumber: string;
  seat: string;
  cabin: string;
  fareClass: string;
  aircraft: string;
  lodgingKind: 'hotel' | 'airbnb' | 'rental' | 'other';
  room: string;
  host: string;
  guests: string;
  carClass: string;
  number: string;
  coach: string;
  travelClass: string;
  ship: string;
  partySize: string;
  venue: string;
  tickets: string;
  transferKind: 'taxi' | 'rideshare' | 'shuttle' | 'private' | 'other';
  spot: string;
  // cost
  price: string;
  currency: string;
  paidWith: '' | 'cash' | 'points' | 'cert' | 'mixed';
  points: string;
  pointsProgram: string;
}

function blank(type: ItemType, date: string): Draft {
  const lodging = type === 'lodging';
  const car = type === 'car';
  return {
    type,
    title: '',
    provider: '',
    confirmation: '',
    phone: '',
    url: '',
    notes: '',
    startPlace: null,
    endPlace: null,
    startDate: date,
    startTime: lodging ? '15:00' : car ? '10:00' : '',
    endDate: lodging || car ? addDays(date, 1) : '',
    endTime: lodging ? '11:00' : car ? '10:00' : '',
    airline: '',
    flightNumber: '',
    seat: '',
    cabin: '',
    fareClass: '',
    aircraft: '',
    lodgingKind: 'hotel',
    room: '',
    host: '',
    guests: '',
    carClass: '',
    number: '',
    coach: '',
    travelClass: '',
    ship: '',
    partySize: '',
    venue: '',
    tickets: '',
    transferKind: 'taxi',
    spot: '',
    price: '',
    currency: 'USD',
    paidWith: '',
    points: '',
    pointsProgram: '',
  };
}

function fromItem(item: Item, places: (id: string | null) => Place | undefined): Draft {
  const d = blank(
    item.type,
    item.startAt != null && item.startTz ? localDate(item.startAt, item.startTz) : '',
  );
  const det = item.details as Record<string, unknown>;
  const str = (k: string) => (det[k] != null ? String(det[k]) : '');
  const cost = item.cost;
  return {
    ...d,
    title: item.title,
    provider: item.provider ?? '',
    confirmation: item.confirmation ?? '',
    phone: item.phone ?? '',
    url: item.url ?? '',
    notes: item.notes,
    startPlace: places(item.startPlaceId) ?? null,
    endPlace: places(item.endPlaceId) ?? null,
    startTime:
      item.startAt != null && item.startTz && !item.allDay
        ? localTime(item.startAt, item.startTz)
        : '',
    endDate: item.endAt != null ? localDate(item.endAt, item.endTz ?? item.startTz ?? 'UTC') : '',
    endTime:
      item.endAt != null && !item.allDay
        ? localTime(item.endAt, item.endTz ?? item.startTz ?? 'UTC')
        : '',
    airline: str('airline'),
    flightNumber: str('flightNumber'),
    seat: str('seat'),
    cabin: item.details.type === 'cruise' ? str('cabin') : str('cabin'),
    fareClass: str('fareClass'),
    aircraft: str('aircraft'),
    lodgingKind: (det.kind as Draft['lodgingKind']) ?? 'hotel',
    room: str('room'),
    host: str('host'),
    guests: str('guests'),
    carClass: str('carClass'),
    number: str('number'),
    coach: str('coach'),
    travelClass: str('class'),
    ship: str('ship'),
    partySize: str('partySize'),
    venue: str('venue'),
    tickets: str('tickets'),
    transferKind: (det.kind as Draft['transferKind']) ?? 'taxi',
    spot: str('spot'),
    price: cost?.amount != null ? (cost.amount / 100).toFixed(2) : '',
    currency: cost?.currency ?? 'USD',
    paidWith: cost?.paidWith ?? '',
    points: cost?.points != null ? String(cost.points) : '',
    pointsProgram: cost?.program ?? '',
  };
}

const opt = (v: string) => (v.trim() ? v.trim() : undefined);

function buildDetails(d: Draft): ItemDetails {
  switch (d.type) {
    case 'flight':
      return {
        type: 'flight',
        airline: d.airline.trim().toUpperCase(),
        flightNumber: d.flightNumber.trim().replace(/^0+/, ''),
        origin: d.startPlace?.iata ?? '',
        destination: d.endPlace?.iata ?? '',
        seat: opt(d.seat.toUpperCase()),
        cabin: opt(d.cabin),
        fareClass: opt(d.fareClass.toUpperCase()),
        aircraft: opt(d.aircraft),
      };
    case 'lodging': {
      const brand = d.lodgingKind === 'hotel' ? hotelBrand(d.title) : null;
      return {
        type: 'lodging',
        kind: d.lodgingKind,
        propertyName: d.title.trim(),
        brand: brand?.brand,
        program: brand?.program,
        room: opt(d.room),
        host: opt(d.host),
        guests: d.guests ? Number(d.guests) || undefined : undefined,
      };
    }
    case 'car':
      return {
        type: 'car',
        company: d.provider.trim() || d.title.trim(),
        carClass: opt(d.carClass),
      };
    case 'train':
    case 'bus':
    case 'ferry':
      return {
        type: d.type,
        carrier: opt(d.provider),
        number: opt(d.number),
        coach: opt(d.coach),
        seat: opt(d.seat),
        class: opt(d.travelClass),
      };
    case 'cruise':
      return { type: 'cruise', line: opt(d.provider), ship: opt(d.ship), cabin: opt(d.cabin) };
    case 'restaurant':
      return {
        type: 'restaurant',
        partySize: d.partySize ? Number(d.partySize) || undefined : undefined,
      };
    case 'activity':
      return { type: 'activity', venue: opt(d.venue), tickets: opt(d.tickets) };
    case 'transfer':
      return { type: 'transfer', kind: d.transferKind, company: opt(d.provider) };
    case 'parking':
      return { type: 'parking', facility: opt(d.title), spot: opt(d.spot) };
    default:
      return { type: 'other' };
  }
}

function buildTitle(d: Draft): string {
  switch (d.type) {
    case 'flight':
      return `${airlineName(d.airline) ?? d.airline.toUpperCase()} ${d.flightNumber.trim().replace(/^0+/, '')}`.trim();
    case 'car':
      return d.provider.trim() || 'Car rental';
    case 'train':
    case 'bus':
    case 'ferry': {
      const base = [d.provider.trim(), d.number.trim()].filter(Boolean).join(' ');
      return (
        base ||
        `${ITEM_TYPE_LABELS[d.type]} to ${d.endPlace?.city ?? d.endPlace?.name ?? 'next stop'}`
      );
    }
    case 'cruise':
      return d.ship.trim() || d.provider.trim() || 'Cruise';
    case 'transfer':
      return d.title.trim() || `Ride to ${d.endPlace?.name ?? 'destination'}`;
    default:
      return d.title.trim() || ITEM_TYPE_LABELS[d.type];
  }
}

function buildCost(d: Draft): Cost | null {
  const amount = d.price ? Math.round(Number(d.price.replace(/[^\d.]/g, '')) * 100) : undefined;
  const points = d.points ? Number(d.points.replace(/[^\d]/g, '')) : undefined;
  if (amount == null && points == null && !d.paidWith) return null;
  return {
    amount: Number.isFinite(amount) ? amount : undefined,
    currency: amount != null ? d.currency.toUpperCase() || 'USD' : undefined,
    paidWith: d.paidWith || undefined,
    points: Number.isFinite(points) ? points : undefined,
    program: opt(d.pointsProgram),
  };
}

const MOVES: ItemType[] = ['flight', 'train', 'bus', 'ferry', 'cruise', 'transfer'];

function validate(d: Draft): string | null {
  if (d.type === 'flight') {
    if (!d.airline.trim() || !d.flightNumber.trim())
      return 'Add the airline code and flight number, like DL 2187.';
    if (!d.startPlace || !d.endPlace) return 'Pick both airports.';
    if (!d.startDate || !d.startTime) return 'Add the departure date and time.';
    if (!d.endTime) return 'Add the arrival time.';
  }
  if (d.type === 'lodging' && !d.title.trim()) return 'Add the name of the place you are staying.';
  if (d.type === 'car' && !d.provider.trim()) return 'Add the rental company.';
  if (!d.startDate) return 'Add a date.';
  return null;
}

export function PlanEditor({
  open,
  onClose,
  tripId,
  itemId,
  initialType,
}: {
  open: boolean;
  onClose: () => void;
  tripId?: string | null;
  itemId?: string;
  initialType?: ItemType | null;
}) {
  const places = useStore(selectPlaceLookup);
  const today = useStore(selectToday);
  const existing = useStore((st) => (itemId ? st.tables.items.get(itemId) : undefined));
  const trip = useStore((st) => (tripId ? selectTripSummary(st, tripId) : undefined));
  const defaultDate = trip?.startDate && trip.startDate > today ? trip.startDate : today;
  const [draft, setDraft] = useState<Draft | null>(() =>
    existing
      ? fromItem(existing, (id) => places(id))
      : initialType
        ? blank(initialType, defaultDate)
        : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCost, setShowCost] = useState(!!existing?.cost);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d, [k]: v };
      // A hotel's name sets its checkout (4 PM at Marriott and Hyatt) unless I changed it.
      if (k === 'title' && d.type === 'lodging' && (d.endTime === '11:00' || d.endTime === '16:00'))
        next.endTime = defaultCheckoutTime(String(v));
      return next;
    });

  const startTz = draft?.startPlace?.tz ?? deviceTimeZone();
  const endTz = (MOVES.includes(draft?.type ?? 'other') ? draft?.endPlace?.tz : null) ?? startTz;
  const zoneNote = useMemo(() => {
    if (!draft) return null;
    const at = Date.now();
    const a = tzAbbr(at, startTz);
    const b = tzAbbr(at, endTz);
    const where = draft.startPlace?.city ?? draft.startPlace?.name;
    if (MOVES.includes(draft.type) && endTz !== startTz)
      return `Departure in ${a}, arrival in ${b}, each local to the airport or station.`;
    return where
      ? `Times are local to ${where} (${a}).`
      : `Times are in ${a}. Pick a place to use its local time.`;
  }, [draft, startTz, endTz]);

  const save = async () => {
    if (!draft) return;
    const problem = validate(draft);
    if (problem) {
      setError(problem);
      void haptics.notify('warning');
      return;
    }
    setSaving(true);
    try {
      const allDay = !draft.startTime;
      const startAt = instantFromLocal(
        draft.startTime ? `${draft.startDate}T${draft.startTime}` : draft.startDate,
        startTz,
      );
      let endAt: number | null = null;
      if (draft.endTime || draft.endDate) {
        const endDate = draft.endDate || draft.startDate;
        endAt = instantFromLocal(draft.endTime ? `${endDate}T${draft.endTime}` : endDate, endTz);
        if (endAt < startAt && draft.type !== 'flight') endAt = null;
        if (draft.type === 'flight' && endAt != null && endAt < startAt) {
          // Arrival typed without a date: the overnight case lands the next day.
          endAt = instantFromLocal(`${addDays(endDate, 1)}T${draft.endTime}`, endTz);
        }
      }
      const base = {
        type: draft.type,
        status: existing?.status ?? ('active' as const),
        title: buildTitle(draft),
        startAt,
        startTz,
        endAt,
        endTz: endAt != null ? endTz : null,
        allDay,
        startPlaceId: draft.startPlace?.id ?? null,
        endPlaceId:
          MOVES.includes(draft.type) || draft.type === 'car'
            ? (draft.endPlace?.id ?? draft.startPlace?.id ?? null)
            : null,
        provider:
          draft.type === 'flight'
            ? (airlineName(draft.airline) ?? draft.airline.toUpperCase())
            : draft.type === 'lodging'
              ? draft.lodgingKind === 'airbnb'
                ? 'Airbnb'
                : (hotelBrand(draft.title)?.brand ?? null)
              : (opt(draft.provider) ?? null),
        confirmation: opt(draft.confirmation.toUpperCase()) ?? null,
        phone: opt(draft.phone) ?? null,
        url: opt(draft.url) ?? null,
        details: buildDetails(draft),
        notes: draft.notes,
        cost: buildCost(draft),
      };
      let targetTrip = existing?.tripId ?? tripId ?? null;
      if (!existing && !targetTrip) targetTrip = await tripForNewItem(base);
      await widenTripFor(targetTrip, base);
      const saved = await put('items', {
        ...(existing ?? {}),
        ...base,
        id: existing?.id ?? newId('itm'),
        tripId: targetTrip,
        tripLocked: existing?.tripLocked ?? !!tripId,
        sourceId: existing?.sourceId ?? null,
      } as Item);
      void haptics.notify('success');
      toast(existing ? 'Plan saved' : 'Plan added');
      onClose();
      if (!existing && saved.tripId && !getState().tables.trips.get(saved.tripId)?.deleted) {
        setTimeout(
          () => navigate(`/trips/${saved.tripId}`, { replace: true, transition: 'none' }),
          0,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That plan couldn't be saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const title = existing
    ? `Edit ${ITEM_TYPE_LABELS[existing.type].toLowerCase()}`
    : draft
      ? `New ${ITEM_TYPE_LABELS[draft.type].toLowerCase()}`
      : 'Add plan';

  return (
    <Sheet open={open} onClose={onClose} title={title} size="full">
      {!draft ? (
        <div className={s.types}>
          {TYPES.map((t) => (
            <button
              key={t.type}
              type="button"
              className={s.type}
              onClick={() => setDraft(blank(t.type, defaultDate))}
            >
              <Icon name={t.icon} size={26} />
              <span>{ITEM_TYPE_LABELS[t.type]}</span>
            </button>
          ))}
        </div>
      ) : (
        <form
          className={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <TypeFields
            draft={draft}
            set={set}
            near={trip?.cities[0]?.name ?? trip?.places[0] ?? null}
          />

          <div className={s.group}>
            <FieldRow>
              <TextField
                label={startLabel(draft.type)}
                type="date"
                value={draft.startDate}
                onChange={(v) => set('startDate', v)}
                required
              />
              <TextField
                label="Time"
                type="time"
                value={draft.startTime}
                onChange={(v) => set('startTime', v)}
              />
            </FieldRow>
            {hasEnd(draft.type) && (
              <FieldRow>
                <TextField
                  label={endLabel(draft.type)}
                  type="date"
                  value={draft.endDate}
                  onChange={(v) => set('endDate', v)}
                />
                <TextField
                  label="Time"
                  type="time"
                  value={draft.endTime}
                  onChange={(v) => set('endTime', v)}
                />
              </FieldRow>
            )}
            {zoneNote && <p className="t-footnote muted">{zoneNote}</p>}
          </div>

          <div className={s.group}>
            <TextField
              label="Confirmation"
              value={draft.confirmation}
              onChange={(v) => set('confirmation', v)}
              autoCapitalize="characters"
              autoComplete="off"
              className="num"
            />
            <FieldRow>
              <TextField
                label="Phone"
                type="tel"
                value={draft.phone}
                onChange={(v) => set('phone', v)}
              />
              <TextField
                label="Website"
                type="url"
                value={draft.url}
                onChange={(v) => set('url', v)}
                placeholder="https://"
              />
            </FieldRow>
          </div>

          {showCost ? (
            <div className={s.group}>
              <FieldRow>
                <TextField
                  label="Price"
                  inputMode="decimal"
                  value={draft.price}
                  onChange={(v) => set('price', v)}
                  placeholder="0.00"
                />
                <TextField
                  label="Currency"
                  value={draft.currency}
                  onChange={(v) => set('currency', v.toUpperCase().slice(0, 3))}
                />
              </FieldRow>
              <Select
                label="Paid with"
                value={draft.paidWith}
                onChange={(v) => set('paidWith', v as Draft['paidWith'])}
                options={[
                  { value: '', label: 'Not set' },
                  { value: 'cash', label: 'Cash or card' },
                  { value: 'points', label: 'Points' },
                  { value: 'cert', label: 'Free night certificate' },
                  { value: 'mixed', label: 'Points and cash' },
                ]}
              />
              {(draft.paidWith === 'points' || draft.paidWith === 'mixed') && (
                <FieldRow>
                  <TextField
                    label="Points"
                    inputMode="numeric"
                    value={draft.points}
                    onChange={(v) => set('points', v)}
                  />
                  <TextField
                    label="Program"
                    value={draft.pointsProgram}
                    onChange={(v) => set('pointsProgram', v)}
                    placeholder="IHG One Rewards"
                  />
                </FieldRow>
              )}
            </div>
          ) : (
            <button type="button" className={s.more} onClick={() => setShowCost(true)}>
              <Icon name="coins" size={18} />
              Add price or points
            </button>
          )}

          <TextArea
            label="Notes"
            value={draft.notes}
            onChange={(v) => set('notes', v)}
            minRows={2}
          />

          {error && (
            <p className={`${s.error} t-footnote`} role="alert">
              {error}
            </p>
          )}
          <div className={s.actions}>
            {!existing && (
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Change type
              </Button>
            )}
            <Button variant="primary" type="submit" disabled={saving}>
              {existing ? 'Save' : 'Add plan'}
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}

function startLabel(t: ItemType) {
  return t === 'lodging'
    ? 'Check in'
    : t === 'car'
      ? 'Pick up'
      : MOVES.includes(t)
        ? 'Departs'
        : 'Date';
}
function endLabel(t: ItemType) {
  return t === 'lodging'
    ? 'Check out'
    : t === 'car'
      ? 'Return'
      : MOVES.includes(t)
        ? 'Arrives'
        : 'Ends';
}
function hasEnd(t: ItemType) {
  return t !== 'restaurant';
}

function TypeFields({
  draft: d,
  set,
  near,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  near: string | null;
}) {
  const home = useStore(selectHome);
  switch (d.type) {
    case 'flight':
      return (
        <div className={s.group}>
          <FieldRow>
            <TextField
              label="Airline code"
              value={d.airline}
              onChange={(v) => set('airline', v.toUpperCase().slice(0, 3))}
              placeholder="DL"
              autoCapitalize="characters"
            />
            <TextField
              label="Flight number"
              inputMode="numeric"
              value={d.flightNumber}
              onChange={(v) => set('flightNumber', v)}
              placeholder="2187"
            />
          </FieldRow>
          <AirportField
            label="From"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            placeholder={
              home?.place.iata && home.place.city
                ? `${home.place.iata} or ${home.place.city}`
                : undefined
            }
          />
          <AirportField label="To" value={d.endPlace} onChange={(p) => set('endPlace', p)} />
          <FieldRow>
            <TextField
              label="Seat"
              value={d.seat}
              onChange={(v) => set('seat', v)}
              autoCapitalize="characters"
            />
            <TextField
              label="Cabin"
              value={d.cabin}
              onChange={(v) => set('cabin', v)}
              placeholder="Main"
            />
          </FieldRow>
          <p className="t-footnote muted">
            Looking flights up by number arrives with live flight tracking.
          </p>
        </div>
      );
    case 'lodging':
      return (
        <div className={s.group}>
          <Segmented
            label="Kind of stay"
            value={d.lodgingKind}
            onChange={(v) => set('lodgingKind', v)}
            options={[
              { value: 'hotel', label: 'Hotel' },
              { value: 'airbnb', label: 'Airbnb' },
              { value: 'rental', label: 'Rental' },
              { value: 'other', label: 'Other' },
            ]}
          />
          <TextField
            label="Name"
            value={d.title}
            onChange={(v) => set('title', v)}
            placeholder={
              d.lodgingKind === 'airbnb' ? 'Airbnb in Linden Hills' : 'Crowne Plaza AiRE'
            }
          />
          <PlaceField
            label="Address"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind="lodging"
            near={near}
            placeholder="Street address or the hotel name and city"
          />
          <FieldRow>
            <TextField
              label={d.lodgingKind === 'airbnb' ? 'Host' : 'Room'}
              value={d.lodgingKind === 'airbnb' ? d.host : d.room}
              onChange={(v) => set(d.lodgingKind === 'airbnb' ? 'host' : 'room', v)}
            />
            <TextField
              label="Guests"
              inputMode="numeric"
              value={d.guests}
              onChange={(v) => set('guests', v)}
            />
          </FieldRow>
        </div>
      );
    case 'car':
      return (
        <div className={s.group}>
          <TextField
            label="Company"
            value={d.provider}
            onChange={(v) => set('provider', v)}
            placeholder="Hertz"
          />
          <AirportOrPlace
            label="Pick up at"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            near={near}
          />
          <AirportOrPlace
            label="Return at"
            value={d.endPlace}
            onChange={(p) => set('endPlace', p)}
            near={near}
            hint="Leave empty to return where you picked it up."
          />
          <TextField
            label="Car"
            value={d.carClass}
            onChange={(v) => set('carClass', v)}
            placeholder="Midsize SUV"
          />
        </div>
      );
    case 'train':
    case 'bus':
    case 'ferry':
    case 'cruise':
      return (
        <div className={s.group}>
          <FieldRow>
            <TextField
              label={d.type === 'cruise' ? 'Cruise line' : 'Company'}
              value={d.provider}
              onChange={(v) => set('provider', v)}
            />
            {d.type === 'cruise' ? (
              <TextField label="Ship" value={d.ship} onChange={(v) => set('ship', v)} />
            ) : (
              <TextField label="Number" value={d.number} onChange={(v) => set('number', v)} />
            )}
          </FieldRow>
          <PlaceField
            label="From"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind={d.type === 'train' ? 'station' : 'port'}
            near={near}
          />
          <PlaceField
            label="To"
            value={d.endPlace}
            onChange={(p) => set('endPlace', p)}
            kind={d.type === 'train' ? 'station' : 'port'}
            near={near}
          />
          <FieldRow>
            <TextField
              label={d.type === 'cruise' ? 'Cabin' : 'Seat'}
              value={d.type === 'cruise' ? d.cabin : d.seat}
              onChange={(v) => set(d.type === 'cruise' ? 'cabin' : 'seat', v)}
            />
            {d.type !== 'cruise' && (
              <TextField
                label="Class"
                value={d.travelClass}
                onChange={(v) => set('travelClass', v)}
              />
            )}
          </FieldRow>
        </div>
      );
    case 'restaurant':
      return (
        <div className={s.group}>
          <TextField
            label="Restaurant"
            value={d.title}
            onChange={(v) => set('title', v)}
            placeholder="Dinner at Lilia"
          />
          <PlaceField
            label="Address"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind="poi"
            near={near}
          />
          <TextField
            label="Party size"
            inputMode="numeric"
            value={d.partySize}
            onChange={(v) => set('partySize', v)}
          />
        </div>
      );
    case 'activity':
      return (
        <div className={s.group}>
          <TextField
            label="What"
            value={d.title}
            onChange={(v) => set('title', v)}
            placeholder="Twins game"
          />
          <PlaceField
            label="Where"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind="poi"
            near={near}
          />
          <TextField
            label="Tickets"
            value={d.tickets}
            onChange={(v) => set('tickets', v)}
            placeholder="Section 112, row 8"
          />
        </div>
      );
    case 'transfer':
      return (
        <div className={s.group}>
          <Segmented
            label="Kind of ride"
            value={d.transferKind}
            onChange={(v) => set('transferKind', v)}
            options={[
              { value: 'taxi', label: 'Taxi' },
              { value: 'rideshare', label: 'Rideshare' },
              { value: 'shuttle', label: 'Shuttle' },
              { value: 'private', label: 'Car service' },
            ]}
          />
          <TextField label="Company" value={d.provider} onChange={(v) => set('provider', v)} />
          <AirportOrPlace
            label="Pick up"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            near={near}
          />
          <AirportOrPlace
            label="Drop off"
            value={d.endPlace}
            onChange={(p) => set('endPlace', p)}
            near={near}
          />
        </div>
      );
    case 'parking':
      return (
        <div className={s.group}>
          <TextField
            label="Where"
            value={d.title}
            onChange={(v) => set('title', v)}
            placeholder="RSW Park and Ride"
          />
          <PlaceField
            label="Address"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind="poi"
            near={near}
          />
          <TextField label="Spot" value={d.spot} onChange={(v) => set('spot', v)} />
        </div>
      );
    default:
      return (
        <div className={s.group}>
          <TextField label="Title" value={d.title} onChange={(v) => set('title', v)} />
          <PlaceField
            label="Where"
            value={d.startPlace}
            onChange={(p) => set('startPlace', p)}
            kind="poi"
            near={near}
          />
        </div>
      );
  }
}

function AirportOrPlace({
  label,
  value,
  onChange,
  near,
  hint,
}: {
  label: string;
  value: Place | null;
  onChange: (p: Place | null) => void;
  near: string | null;
  hint?: string;
}) {
  const [mode, setMode] = useState<'airport' | 'address'>(
    value && value.kind !== 'airport' ? 'address' : 'airport',
  );
  return (
    <div className={s.group}>
      <Segmented
        label={label}
        value={mode}
        onChange={setMode}
        options={[
          { value: 'airport', label: 'Airport' },
          { value: 'address', label: 'Address' },
        ]}
      />
      {mode === 'airport' ? (
        <AirportField
          label={label}
          value={value?.kind === 'airport' ? value : null}
          onChange={onChange}
        />
      ) : (
        <PlaceField
          label={label}
          value={value?.kind !== 'airport' ? value : null}
          onChange={onChange}
          kind="address"
          near={near}
        />
      )}
      {hint && <p className="t-footnote muted">{hint}</p>}
    </div>
  );
}
